export const COMMITMENTS_CHAT_ID = 'pwa-auto-commitments'

export type CommitmentBoardTableRow = {
  id: string
  task: string
  promisedBy: string
  email: string
  promisedOn: string
  dueDate: string
  remind: string
  project: string
  status: string
  review: string
  snooze: string
  chases: string
  notes: string
}

export type CommitmentBoardSegment =
  | { kind: 'markdown'; body: string }
  | { kind: 'commitment'; id: string; body: string }
  | { kind: 'commitment_table'; rows: CommitmentBoardTableRow[] }

const ACTIVE_SECTION_RE = /^##\s+.*\b(Overdue|Due today|Due this week|Later|No date)\b.*\(\d+\)\s*$/i
const SECTION_RE = /^##\s+/
const COMMITMENT_ITEM_RE = /^- \*\*/
const COMMITMENT_ID_RE = /`(c-\d{6}-[A-Za-z0-9]+)`/
const TABLE_ROW_RE = /^\s*\|.*\|\s*$/
const TABLE_SEPARATOR_RE = /^\s*\|(?:\s*:?-{3,}:?\s*\|)+\s*$/

export function isCommitmentBoardMessage(chatId: string, body: string): boolean {
  return chatId === COMMITMENTS_CHAT_ID && body.trimStart().startsWith('# Commitments')
}

export function splitCommitmentBoardBody(body: string): CommitmentBoardSegment[] {
  const lines = body.split('\n')
  const segments: CommitmentBoardSegment[] = []
  const markdownLines: string[] = []
  let inActiveSection = false

  function flushMarkdown() {
    const markdown = markdownLines.join('\n').trim()
    if (markdown) segments.push({ kind: 'markdown', body: markdown })
    markdownLines.length = 0
  }

  for (let i = 0; i < lines.length;) {
    const line = lines[i]

    if (isCommitmentTableHeader(line) && TABLE_SEPARATOR_RE.test(lines[i + 1] ?? '')) {
      flushMarkdown()
      const headerCells = splitMarkdownTableRow(line).map(normalizeHeader)
      const rows: CommitmentBoardTableRow[] = []
      let j = i + 2
      while (j < lines.length && TABLE_ROW_RE.test(lines[j])) {
        const row = rowFromTableCells(headerCells, splitMarkdownTableRow(lines[j]))
        if (row) rows.push(row)
        j += 1
      }
      if (rows.length > 0) {
        segments.push({ kind: 'commitment_table', rows })
      }
      i = j
      while (i < lines.length && lines[i].trim() === '') i += 1
      continue
    }

    if (line.trim() === '---') {
      inActiveSection = false
    } else if (SECTION_RE.test(line)) {
      inActiveSection = ACTIVE_SECTION_RE.test(line)
    }

    if (inActiveSection && COMMITMENT_ITEM_RE.test(line)) {
      const blockLines: string[] = []
      let j = i
      while (j < lines.length) {
        const next = lines[j]
        if (j > i && (next.trim() === '' || SECTION_RE.test(next) || COMMITMENT_ITEM_RE.test(next))) {
          break
        }
        blockLines.push(next)
        j += 1
      }

      const block = blockLines.join('\n').trim()
      const id = extractCommitmentId(block)
      if (id) {
        flushMarkdown()
        segments.push({ kind: 'commitment', id, body: block })
        i = j
        while (i < lines.length && lines[i].trim() === '') i += 1
        continue
      }
    }

    markdownLines.push(line)
    i += 1
  }

  flushMarkdown()
  return segments
}

export function extractCommitmentId(text: string): string | null {
  return COMMITMENT_ID_RE.exec(text)?.[1] ?? null
}

function isCommitmentTableHeader(line: string): boolean {
  if (!TABLE_ROW_RE.test(line)) return false
  const headers = splitMarkdownTableRow(line).map(normalizeHeader)
  return headers.includes('id')
    && headers.includes('task')
    && headers.includes('promised by')
    && headers.includes('remind')
}

function rowFromTableCells(headers: string[], cells: string[]): CommitmentBoardTableRow | null {
  const get = (name: string) => cleanCell(cells[headers.indexOf(name)] ?? '')
  const id = extractCommitmentId(cells[headers.indexOf('id')] ?? '') ?? get('id')
  if (!id) return null
  return {
    id,
    task: get('task'),
    promisedBy: get('promised by'),
    email: get('email'),
    promisedOn: get('promised on'),
    dueDate: get('due date'),
    remind: get('remind'),
    project: get('project'),
    status: get('status'),
    review: get('review'),
    snooze: get('snooze'),
    chases: get('chases'),
    notes: get('notes'),
  }
}

function splitMarkdownTableRow(row: string): string[] {
  const text = row.trim().replace(/^\|/, '').replace(/\|$/, '')
  const cells: string[] = []
  let current = ''
  let escaped = false
  for (const ch of text) {
    if (escaped) {
      current += ch
      escaped = false
      continue
    }
    if (ch === '\\') {
      escaped = true
      continue
    }
    if (ch === '|') {
      cells.push(current.trim())
      current = ''
      continue
    }
    current += ch
  }
  cells.push(current.trim())
  return cells
}

function normalizeHeader(value: string): string {
  return cleanCell(value).toLowerCase()
}

function cleanCell(value: string): string {
  return value
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/\s+/g, ' ')
    .trim()
}

export function formatLocalDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function formatLocalDateTime(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${formatLocalDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}
